import React, { useCallback, useMemo } from "react";

import { useI18n } from "../../../../hooks/i18n-hooks";

import VStack from "../../../../ui_atoms/VStack";
import ScreenCard, { ButtonType } from "../../../../screens/ScreenCard";
import { BodyMediumText, LinkSmallTextBold, TitleMediumText } from "../../../FormattedText";

import ActionButton from "../../../ActionButton";
import { useTheme } from "../../../../hooks/use-theme";
import Gap from "../../../../ui_atoms/Gap";
import Separator from "../../../../ui_atoms/separator";
import AutoSizeImage from "../../../AutoSizeImage";
import { useIntercom } from "../../../../hooks/intercom";
import { useCurrentProfile } from "../../../../hooks/use-current-profile";
import { ClientProfile } from "../../../../__generated__/graphql";
import { getPayFirstDaysSetAndLabel, getPayRemainingPercentsSetAndLabel } from "../helper";

const IJMClientViewer: React.FC = () => {
    const i18n = useI18n();
    const { colors } = useTheme();

    const currentClientProfile = useCurrentProfile<ClientProfile>();
    const { presentMessageComposer, presentArticle } = useIntercom();

    const onValidate = useCallback(async () => {
        await presentMessageComposer();
    }, [presentMessageComposer]);

    const buttons = useMemo<ButtonType[]>(() => {
        return [
            {
                title: i18n.t("profile.contact_support"),
                onPress: onValidate,
                mode: "secondary",
            },
        ];
    }, [i18n, onValidate]);

    return (
        <ScreenCard buttons={buttons}>
            <VStack>
                <VStack h="2/3">
                    <BodyMediumText>{i18n.t("ijm.editor.root.text_1")}</BodyMediumText>
                    <LinkSmallTextBold
                        onPress={() => {
                            void presentArticle("IJMGroupClient");
                        }}
                    >
                        {i18n.t("know_more")}
                    </LinkSmallTextBold>
                </VStack>
                <Separator v={1 / 2} />

                <VStack h="2/3">
                    {/* Group select */}
                    <TitleMediumText>{i18n.t("ijm.editor.root.title_2")}</TitleMediumText>
                    <Gap gap={0.5} />

                    <BodyMediumText>1. {i18n.t("ijm.editor.group_select.title")}</BodyMediumText>
                    <Gap gap={1 / 4} />
                    <ActionButton
                        text={
                            currentClientProfile.client.ijmGroup?.name ||
                            i18n.t("ijm.ijm_group.place_holder")
                        }
                        textColor={colors.greyAccessible}
                        textWidth="medium"
                    />
                    <Gap gap={1} />
                    <BodyMediumText>2. {i18n.t("ijm.editor.waiting_select.title")}</BodyMediumText>
                    <Gap gap={1 / 4} />
                    <ActionButton
                        text={i18n.t(
                            getPayFirstDaysSetAndLabel(
                                currentClientProfile.client.ijmPayFirstDays,
                            )[1],
                        )}
                        textColor={colors.greyAccessible}
                        textWidth="medium"
                    />
                    <Gap gap={1} />

                    {/* First days coverage */}
                    <BodyMediumText>3. {i18n.t("ijm.editor.cover_select.title")}</BodyMediumText>
                    <Gap gap={1 / 4} />
                    <ActionButton
                        text={i18n.t(
                            getPayRemainingPercentsSetAndLabel(
                                currentClientProfile.client.ijmPayRemainingPercents,
                            )[1],
                        )}
                        textColor={colors.greyAccessible}
                        textWidth="medium"
                        numberOfLines={3}
                    />
                    <VStack alignment="center" style={{ opacity: 0.5 }}>
                        <Gap gap={3} />
                        <BodyMediumText>{i18n.t("ijm.editor.root.collaboration")}</BodyMediumText>
                        <Gap gap={1 / 2} />
                        <AutoSizeImage
                            source={require("../../../../assets/images/groupe_mutuel.png")}
                            fixedHeight={40}
                        />
                    </VStack>
                </VStack>
            </VStack>
        </ScreenCard>
    );
};

export default IJMClientViewer;
